import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { paymentTransactionsAPI } from '../../services/api';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardHeader,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

import { PaymentTransaction } from '../../services/api';

// Extend the PaymentTransaction type to include any additional fields needed for the UI
type Transaction = Omit<PaymentTransaction, 'amount'> & {
  amount: string; // Override to string for display
  currency: string;
  payment_date: string;
  customer_name: string;
  customer_email: string;
  customer_mobile?: string;
  booking_date?: string;
  booking_status?: string;
};

interface ApiResponse<T> {
  data: T[];
  total: number;
  message?: string;
  success: boolean;
}

const PaymentTransactions: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  type StatusFilter = 'pending' | 'success' | 'failed' | 'refunded' | '';
  
  const [filters, setFilters] = useState<{
    status: StatusFilter;
    search: string;
  }>({
    status: '',
    search: ''
  });

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await paymentTransactionsAPI.getAll({
        page: page + 1, // API is 1-based, Material-UI is 0-based
        limit: rowsPerPage,
        status: filters.status as 'pending' | 'success' | 'failed',
        search: filters.search,
      });

      // Ensure response.data exists and is an array before mapping
      const transactionsData = response.data && Array.isArray(response.data) ? response.data : [];

      // Map API response to Transaction type
      const transactions = transactionsData
        .filter(tx => tx && typeof tx === 'object') // Filter out null/undefined/non-object items
        .map(tx => ({
          ...tx,
          amount: tx.amount ? tx.amount.toString() : '0',
          currency: 'USD', // Default currency, adjust as needed
          payment_date: tx.created_at || new Date().toISOString(), // Use created_at as payment_date
          customer_name: tx.customer_name || 'Unknown',
          customer_email: tx.customer_email || 'N/A',
          booking_id: tx.booking_id || 'N/A',
          status: tx.status || 'unknown',
        }));

      setTransactions(transactions);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch transactions';
      setError(errorMessage);
      setTransactions([]); // Clear transactions on error
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [page, rowsPerPage, filters.status, filters.search]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchTransactions();
  };

  const handleReset = () => {
    setFilters({
      status: '',
      search: '',
    });
    setPage(0);
    // No need to call fetchTransactions here, it will be triggered by the useEffect
  };

  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), 'dd/MM/yyyy');
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 0 }}>
     
      <Typography variant="h4"  fontWeight="bold" mb={3}>
        Payment Transactions
      </Typography>
      <Card sx={{p:0}}>
        <CardContent sx={{p:0}}>
          <Box sx={{ display: 'flex', gap: 2, mb: 0, flexWrap: 'wrap',p:2 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search transactions..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <TextField
              select
              variant="outlined"
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                status: e.target.value as StatusFilter
              }))}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
            </TextField>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{  borderRadius: 2, boxShadow: 0, p: 0 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Transaction ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                      </Alert>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchTransactions}
                        disabled={loading}
                      >
                        Retry
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        {filters.status || filters.search ? 'No matching transactions found' : 'No transactions available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.payment_transaction_id} hover>
                      <TableCell>#{transaction.payment_id}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.customer_name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {transaction.customer_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{transaction.booking_number}</TableCell>
                      <TableCell>
                        {process.env.REACT_APP_SITE_CURRENCY}{(parseFloat(transaction.amount || '0'))}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status ? transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1) : 'Unknown'}
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{transaction.payment_date ? formatDate(transaction.payment_date) : 'N/A'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 1 }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentTransactions;
